import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useHistory } from 'react-router';
import { History } from 'history';

import {
  useQuoteFlowDispatch,
  useFunnelState,
} from 'scenes/common/contexts/quoteFlowContext';
import { getApiData } from 'scenes/common/helpers/apiData';
import { useMarketingCampaignParams } from 'scenes/common/hooks/useMarketingCampaignParams';
import { useSystemConfig } from 'scenes/common/hooks/useSystemConfig';
import {
  MotorFullPageProps,
  MotorFunnelState,
  MotorFormAnswer,
  MotorCoverType,
  MotorFormValues,
  MotorStepType,
} from 'scenes/motor/types';
import analytics from 'scenes/motor/analytics';
import { useReferenceData } from 'scenes/motor/hooks/useReferenceData';
import { useVehicle } from 'scenes/motor/hooks/useVehicle';
import useActions from 'scenes/motor/hooks/useActions';
import { useCoverTypeParam } from 'scenes/motor/hooks/useCoverTypeParam';
import { mapDefaultCoverOption } from 'scenes/motor/actions';
import {
  FlowAction,
  FunnelType,
  InsuranceName,
} from 'scenes/common/flows/types';
import { isUnderMaintenance } from 'scenes/common/selectors/configSelectors';
import redirectToOutagePage from 'scenes/outage/helpers/config';
import { ApiErrorReasonType } from 'scenes/common/types';

export const withMotorFunnel = (
  FullPageComponent: FunctionComponent<MotorFullPageProps>
): FunctionComponent => () => {
  const funnelState = useFunnelState<MotorFunnelState>();
  const dispatch = useQuoteFlowDispatch();

  const handleAnswer = useCallback(
    (answer: MotorFormAnswer, autoMergeFormToState: boolean = true) => {
      // Enable this if you need to debug actions
      const action: FlowAction<MotorFormValues, MotorStepType> = {
        ...answer,
        sourceStep: funnelState.step,
        autoMergeFormToState,
      };
      dispatch(action);
    },
    [dispatch, funnelState.step]
  );

  const referenceData = useReferenceData();
  const vehicleData = useVehicle();

  const {
    onNext,
    onNextNoAutoFormMerge,
    onPrevious,
    regoSearch,
    onSaveQuote,
    onEditDriver,
    onCreateQuote,
    onBpayPayment,
    onResumeQuote,
    onRestartQuote,
    onUpdateExcess,
    onRemoveDriver,
    onCompleteQuote,
    onClearApiErrors,
    onUpdatePromoCode,
    onEditQuoteDetails,
    onEditCoverDetails,
    onUpdatePaymentDate,
    onEditReviewDriver,
    onUpdateDriver,
    onEditPolicyDetails,
    onCreditCardPayment,
    onResetToMarketValue,
    onUpdateQuoteDetails,
    onDirectDebitPayment,
    onRequestVehicleValue,
    onUpdatePolicyStartDate,
    onInsuranceTypeSwitch,
    onRetrieveCampaign,
    onSetPrefilledPromoCode,
    onSetColesCampaignCode,
    onUpdateOptionalExtras,
    onSelectInsuranceOption,
    onUpdateFlybuys,
    onSetDefaultCoverOption,
    onUpdateToPolicyDetails,
    onSwitchPaymentFrequency,
    onGetConfig,
    onErrorRecoveryDeclined,
  } = useActions(handleAnswer, getApiData(referenceData));

  const history: History = useHistory();

  const [qid, pid, cid] = useMarketingCampaignParams(
    onSetPrefilledPromoCode,
    onRetrieveCampaign,
    onSetColesCampaignCode,
    InsuranceName.Car
  );

  useSystemConfig(onGetConfig);

  const { coverTypeFromUrl, resetDefaultCoverType } = useCoverTypeParam(
    onSetDefaultCoverOption
  );

  const [showSaveQuoteModal, setShowSaveQuoteModal] = useState(false);

  const onClickSaveQuoteModal = () => {
    if (!showSaveQuoteModal) {
      analytics().saveQuote(funnelState);
    }
    setShowSaveQuoteModal(!showSaveQuoteModal);
  };

  const isPreselectedCoverTypeSetFromUrl = (
    urlCoverType: MotorCoverType | undefined,
    preselectedCoverType: MotorCoverType
  ): boolean => urlCoverType === preselectedCoverType;

  const errorRecoveryActions: { [key: string]: { recoveryFn: Function } } = {
    [ApiErrorReasonType.InvalidCoverForSumInsured]: {
      recoveryFn: () => resetDefaultCoverType(MotorCoverType.Comprehensive),
    },
  };

  useEffect(() => {
    if (isUnderMaintenance(funnelState.apiResponses.config, FunnelType.Motor)) {
      redirectToOutagePage(history);
    }
  }, [funnelState, history]);

  useEffect(() => {
    // This condition makes sure the events are only fired when query parameters match the state
    // this avoids it being fired before the state is initialised
    if (
      (!coverTypeFromUrl ||
        isPreselectedCoverTypeSetFromUrl(
          mapDefaultCoverOption(coverTypeFromUrl)?.preselectedCoverType,
          funnelState.quoteDetails.preselectedCoverType
        )) &&
      qid === funnelState.quoteDetails.prefilledCampaignId &&
      pid === funnelState.quoteDetails.prefilledPromoCode &&
      cid === funnelState.quoteDetails.colesCampaignCode
    ) {
      analytics().viewLoaded(funnelState, getApiData(referenceData));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    coverTypeFromUrl,
    qid,
    pid,
    cid,
    history,
    funnelState.step,
    funnelState.flow,
    funnelState.quoteDetails.preselectedCoverType,
    funnelState.quoteDetails.prefilledCampaignId,
    funnelState.quoteDetails.prefilledPromoCode,
    funnelState.quoteDetails.colesCampaignCode,
  ]);

  return (
    <FullPageComponent
      onNext={onNext}
      onNextNoAutoFormMerge={onNextNoAutoFormMerge}
      onPrevious={onPrevious}
      funnelState={funnelState}
      vehicleData={vehicleData}
      onRegoSearch={regoSearch}
      onSaveQuote={onSaveQuote}
      flowType={funnelState.flow}
      stepType={funnelState.step}
      onEditDriver={onEditDriver}
      referenceData={referenceData}
      onCreateQuote={onCreateQuote}
      onBpayPayment={onBpayPayment}
      onResumeQuote={onResumeQuote}
      onRestartQuote={onRestartQuote}
      onUpdateExcess={onUpdateExcess}
      onRemoveDriver={onRemoveDriver}
      onCompleteQuote={onCompleteQuote}
      onClearApiErrors={onClearApiErrors}
      onUpdatePromoCode={onUpdatePromoCode}
      onEditQuoteDetails={onEditQuoteDetails}
      onEditCoverDetails={onEditCoverDetails}
      onUpdatePaymentDate={onUpdatePaymentDate}
      onEditReviewDriver={onEditReviewDriver}
      onEditPolicyDetails={onEditPolicyDetails}
      onCreditCardPayment={onCreditCardPayment}
      onResetToMarketValue={onResetToMarketValue}
      onUpdateQuoteDetails={onUpdateQuoteDetails}
      onDirectDebitPayment={onDirectDebitPayment}
      onRequestVehicleValue={onRequestVehicleValue}
      onUpdatePolicyStartDate={onUpdatePolicyStartDate}
      onInsuranceTypeSwitch={onInsuranceTypeSwitch}
      onUpdateOptionalExtras={onUpdateOptionalExtras}
      onSelectInsuranceOption={onSelectInsuranceOption}
      onUpdateToPolicyDetails={onUpdateToPolicyDetails}
      onClickSaveQuoteModal={onClickSaveQuoteModal}
      showSaveQuoteModal={showSaveQuoteModal}
      onSwitchPaymentFrequency={onSwitchPaymentFrequency}
      onUpdateFlybuys={onUpdateFlybuys}
      onUpdateDriver={onUpdateDriver}
      onErrorRecoveryDeclined={onErrorRecoveryDeclined}
      onErrorRecovery={errorRecoveryActions}
    />
  );
};

export default withMotorFunnel;
